import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/apiClient";
import Chromatogram from "@/components/Chromatogram";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { History as HistoryIcon, GitCompare, Eye, X, FlaskConical, Download } from "lucide-react";
import { toast } from "sonner";
import { exportRunPdf } from "@/lib/pdfReport";

function useRefMaps() {
  const { data: columns = [] } = useQuery({ queryKey: ["columns"], queryFn: () => api.get("/reference/columns").then((r) => r.data) });
  const { data: analytes = [] } = useQuery({ queryKey: ["analytes"], queryFn: () => api.get("/reference/analytes").then((r) => r.data) });
  const colMap = Object.fromEntries(columns.map((c) => [c.id, c.name]));
  const anMap = Object.fromEntries(analytes.map((a) => [a.id, a.name]));
  return { colMap, anMap };
}

function ReplayCard({ expId, colMap, anMap, compact }) {
  const { data, isLoading } = useQuery({ queryKey: ["replay", expId], queryFn: () => api.get(`/lab/experiments/${expId}/replay`).then((r) => r.data) });
  if (isLoading || !data) return <Card className="p-5 rounded-md text-sm text-muted-foreground" data-testid={`replay-loading-${expId}`}>Memuat eksperimen…</Card>;
  const cfg = data.config || {};
  return (
    <Card className="p-4 rounded-md space-y-3" data-testid={`replay-card-${expId}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-heading font-bold text-sm">{colMap[cfg.column_id] || cfg.column_id}</div>
          <div className="text-xs text-muted-foreground font-data">
            {cfg.elution_mode === "gradient" ? "Gradien" : "Isokratik"}
            {cfg.elution_mode === "gradient"
              ? ` ${cfg.gradient?.start_percent}→${cfg.gradient?.end_percent}%`
              : ` ${cfg.percent_organic}%`}
            {" · "}pH {cfg.buffer_ph} · {cfg.flow_rate_ml_min} mL/mnt · {new Date(data.created_at).toLocaleString("id-ID")}
          </div>
        </div>
        <Badge className="font-data">Skor {data.score}</Badge>
      </div>

      <Chromatogram
        trace={data.trace}
        cursor={data.runtime_min}
        runtime={data.runtime_min}
        peaks={data.peaks}
        done
        elution={{ mode: cfg.elution_mode, percent_organic: cfg.percent_organic, gradient: cfg.gradient }}
        height={compact ? 240 : 320}
      />

      <div className="flex justify-end">
        <Button variant="outline" size="sm" className="gap-2" onClick={() => exportRunPdf({ data, config: cfg, colMap })} data-testid={`export-pdf-${expId}`}>
          <Download className="h-4 w-4" /> Unduh PDF
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Analit</TableHead>
            <TableHead className="text-right">tR</TableHead>
            <TableHead className="text-right">N</TableHead>
            <TableHead className="text-right">Tailing</TableHead>
            <TableHead className="text-right">Rs</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.peaks.map((p) => (
            <TableRow key={p.analyte_id} className="font-data text-sm">
              <TableCell className="font-sans font-medium">{p.name}</TableCell>
              <TableCell className="text-right">{p.tR}</TableCell>
              <TableCell className="text-right">{p.N}</TableCell>
              <TableCell className={`text-right ${p.tailing >= 0.9 && p.tailing <= 1.5 ? "text-emerald-600" : "text-destructive"}`}>{p.tailing}</TableCell>
              <TableCell className="text-right">{p.resolution_prev ?? "–"}</TableCell>
            </TableRow>
          ))}
          {(data.not_eluted || []).map((p) => (
            <TableRow key={p.analyte_id} className="font-data text-sm opacity-60">
              <TableCell className="font-sans">{p.name}</TableCell>
              <TableCell colSpan={4} className="text-muted-foreground text-xs">Tidak terelusi</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

export default function History() {
  const { colMap, anMap } = useRefMaps();
  const { data: experiments = [], isLoading } = useQuery({ queryKey: ["my-experiments"], queryFn: () => api.get("/lab/experiments").then((r) => r.data) });
  const [selected, setSelected] = useState([]);
  const [compareIds, setCompareIds] = useState(null);
  const [singleId, setSingleId] = useState(null);

  const toggle = (id) => {
    setSelected((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= 3) { toast.error("Maksimal 3 eksperimen untuk dibandingkan"); return s; }
      return [...s, id];
    });
  };

  const samplesText = (cfg) => (cfg.samples || []).map((s) => anMap[s.analyte_id] || s.analyte_id).join(", ");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl flex items-center gap-2">
            <HistoryIcon className="h-7 w-7 text-primary" /> Riwayat Eksperimen
          </h1>
          <p className="text-muted-foreground mt-1">Buka kembali eksperimen Anda atau bandingkan kromatogram berdampingan.</p>
        </div>
        <Button
          className="gap-2"
          disabled={selected.length < 2}
          onClick={() => { setCompareIds(selected); setSingleId(null); }}
          data-testid="compare-button"
        >
          <GitCompare className="h-4 w-4" /> Bandingkan ({selected.length})
        </Button>
      </div>

      {compareIds && (
        <div className="space-y-3 fade-in-up">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-lg">Perbandingan</h2>
            <Button variant="ghost" size="sm" className="gap-1" onClick={() => setCompareIds(null)} data-testid="close-compare"><X className="h-4 w-4" /> Tutup</Button>
          </div>
          <div className={`grid gap-4 ${compareIds.length >= 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`} data-testid="compare-grid">
            {compareIds.map((id) => <ReplayCard key={id} expId={id} colMap={colMap} anMap={anMap} compact />)}
          </div>
        </div>
      )}

      {singleId && (
        <div className="space-y-3 fade-in-up">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-lg">Detail Eksperimen</h2>
            <Button variant="ghost" size="sm" className="gap-1" onClick={() => setSingleId(null)}><X className="h-4 w-4" /> Tutup</Button>
          </div>
          <ReplayCard expId={singleId} colMap={colMap} anMap={anMap} />
        </div>
      )}

      {isLoading && <p className="text-muted-foreground">Memuat…</p>}

      {!isLoading && experiments.length === 0 && (
        <Card className="p-10 rounded-md text-center" data-testid="history-empty">
          <FlaskConical className="h-10 w-10 text-primary/40 mx-auto mb-3" />
          <p className="text-muted-foreground">Belum ada eksperimen. Jalankan eksperimen di Lab Virtual terlebih dahulu.</p>
        </Card>
      )}

      {experiments.length > 0 && (
        <Card className="rounded-md overflow-x-auto" data-testid="history-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Pilih</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead>Kolom</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Analit</TableHead>
                <TableHead className="text-right">Skor</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {experiments.map((e) => (
                <TableRow key={e.id} data-testid={`history-row-${e.id}`}>
                  <TableCell>
                    <Checkbox checked={selected.includes(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`select-${e.id}`} />
                  </TableCell>
                  <TableCell className="font-data text-sm whitespace-nowrap">{new Date(e.created_at).toLocaleString("id-ID")}</TableCell>
                  <TableCell className="text-sm">{colMap[e.config?.column_id] || e.config?.column_id}</TableCell>
                  <TableCell className="text-sm">{e.config?.elution_mode === "gradient" ? "Gradien" : "Isokratik"}</TableCell>
                  <TableCell className="text-sm max-w-[220px] truncate">{samplesText(e.config || {})}</TableCell>
                  <TableCell className="text-right font-data font-semibold">{e.score ?? "–"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="gap-1" onClick={() => { setSingleId(e.id); setCompareIds(null); }} data-testid={`view-${e.id}`}>
                      <Eye className="h-4 w-4" /> Lihat
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
